import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Key } from "lucide-react";
import { toast } from "sonner";

export default function StaffAndTransport() {
  const [tab, setTab] = useState("staff");
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [uf, setUf] = useState({ name: "", email: "", password: "", role: "teacher", phone: "", linked_student_id: "" });
  const [rf, setRf] = useState({ route_name: "", driver_name: "", driver_phone: "", vehicle_no: "", stops: "", fee: 0, capacity: 40 });
  const [pwdOpen, setPwdOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [students, setStudents] = useState([]);

  const load = async () => {
    const [u, r, st] = await Promise.all([api.get("/users"), api.get("/bus-routes"), api.get("/students")]);
    setUsers(u.data); setRoutes(r.data); setStudents(st.data);
  };
  useEffect(() => { load(); }, []);

  const addStaff = async (e) => {
    e.preventDefault();
    try { await api.post("/users", { ...uf, linked_student_id: uf.linked_student_id || null }); toast.success("Staff added"); setUf({ name: "", email: "", password: "", role: "teacher", phone: "", linked_student_id: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delStaff = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/users/${id}`); load(); };
  const resetPwd = async () => {
    try { await api.post("/auth/reset-user-password", { user_id: target.id, new_password: newPwd }); toast.success("Password reset"); setPwdOpen(false); setNewPwd(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addRoute = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...rf, stops: rf.stops.split(",").map(x => x.trim()).filter(Boolean), fee: Number(rf.fee), capacity: Number(rf.capacity) };
      await api.post("/bus-routes", payload); toast.success("Route added");
      setRf({ route_name: "", driver_name: "", driver_phone: "", vehicle_no: "", stops: "", fee: 0, capacity: 40 }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delRoute = async (id) => { await api.delete(`/bus-routes/${id}`); load(); };

  return (
    <DashLayout variant="school" title="Staff & Transport">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff (Teachers, Drivers, etc.)</TabsTrigger>
          <TabsTrigger value="bus" data-testid="tab-bus">Bus Routes</TabsTrigger>
        </TabsList>

        <TabsContent value="staff">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end mb-4">
            <Input placeholder="Name *" value={uf.name} onChange={(e) => setUf({ ...uf, name: e.target.value })} data-testid="staff-name" />
            <Input placeholder="Email *" type="email" value={uf.email} onChange={(e) => setUf({ ...uf, email: e.target.value })} data-testid="staff-email" />
            <Input placeholder="Password *" value={uf.password} onChange={(e) => setUf({ ...uf, password: e.target.value })} data-testid="staff-password" />
            <Select value={uf.role} onValueChange={(v) => setUf({ ...uf, role: v })}>
              <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="teacher">Teacher</SelectItem><SelectItem value="parent">Parent (login for guardians)</SelectItem></SelectContent>
            </Select>
            <Button onClick={addStaff} className="bg-blue-700 hover:bg-blue-800" data-testid="staff-add"><Plus className="w-4 h-4 mr-1" />Add Staff</Button>
          </Card>
          {uf.role === "parent" && (
            <Card className="p-4 mb-4">
              <Label className="mb-1 block text-xs">Link to Student (required for Parent role — enables Parent Portal)</Label>
              <Select value={uf.linked_student_id} onValueChange={(v) => setUf({ ...uf, linked_student_id: v })}>
                <SelectTrigger data-testid="staff-link-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · Class {s.class_name}</SelectItem>)}</SelectContent>
              </Select>
            </Card>
          )}
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{users.map(u => (
                <TableRow key={u.id} data-testid={`row-staff-${u.id}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell><TableCell>{u.email}</TableCell><TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setTarget(u); setPwdOpen(true); }} data-testid={`reset-staff-${u.id}`}><Key className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => delStaff(u.id)} data-testid={`del-staff-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="bus">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end mb-4">
            <Input placeholder="Route Name *" value={rf.route_name} onChange={(e) => setRf({ ...rf, route_name: e.target.value })} data-testid="br-name" />
            <Input placeholder="Vehicle No *" value={rf.vehicle_no} onChange={(e) => setRf({ ...rf, vehicle_no: e.target.value })} data-testid="br-vehicle" />
            <Input placeholder="Driver Name" value={rf.driver_name} onChange={(e) => setRf({ ...rf, driver_name: e.target.value })} data-testid="br-driver" />
            <Input placeholder="Driver Phone" value={rf.driver_phone} onChange={(e) => setRf({ ...rf, driver_phone: e.target.value })} data-testid="br-phone" />
            <Input placeholder="Stops (comma separated)" value={rf.stops} onChange={(e) => setRf({ ...rf, stops: e.target.value })} className="md:col-span-2" data-testid="br-stops" />
            <Input type="number" placeholder="Monthly Fee" value={rf.fee} onChange={(e) => setRf({ ...rf, fee: e.target.value })} data-testid="br-fee" />
            <Button onClick={addRoute} className="bg-blue-700 hover:bg-blue-800" data-testid="br-add"><Plus className="w-4 h-4 mr-1" />Add Route</Button>
          </Card>
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>Route</TableHead><TableHead>Vehicle</TableHead><TableHead>Driver</TableHead><TableHead>Stops</TableHead><TableHead className="text-right">Fee</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{routes.map(r => (
                <TableRow key={r.id} data-testid={`row-route-${r.id}`}>
                  <TableCell className="font-semibold">{r.route_name}</TableCell><TableCell>{r.vehicle_no}</TableCell>
                  <TableCell>{r.driver_name}<div className="text-xs text-slate-500">{r.driver_phone}</div></TableCell>
                  <TableCell className="text-sm">{(r.stops || []).join(" → ")}</TableCell>
                  <TableCell className="text-right tabular-nums">₹{(r.fee || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delRoute(r.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>
              ))}</TableBody></Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent><DialogHeader><DialogTitle>Reset password — {target?.email}</DialogTitle></DialogHeader>
          <Input placeholder="New password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="staff-newpwd" />
          <DialogFooter><Button onClick={resetPwd} className="bg-slate-900 hover:bg-slate-800" data-testid="staff-newpwd-submit">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
